import { Box, Table, Tbody, Th, Thead, Tr } from '@chakra-ui/react';
import BulkActionBar, { BulkActionBarItem } from '../../../components/BulkActionBar';
import type { PurchaseOrderEntry } from '../../../types/inventory/purchaseOrders';
import type { PurchaseOrderPartAction } from './PurchaseOrderPartCells';
import { PO_CONTROL_CELL_PROPS, PO_TH_PROPS, PurchaseOrderRow } from './PurchaseOrderRow';

const BULK_ACTIONS: BulkActionBarItem[] = [
    { id: 'confirm', label: 'Confirm', iconName: 'check', success: true },
    { id: 'reject', label: 'Reject', iconName: 'x', danger: true },
];

const HEADER_TH_PROPS = {
    ...PO_TH_PROPS,
    bg: 'gray.50',
    borderBottomWidth: 0,
    boxShadow: 'inset 0 -1px 0 0 var(--chakra-colors-gray-200)',
} as const;

export interface PurchaseOrdersTableProps {
    requests: PurchaseOrderEntry[];
    focusedPurchaseOrderId: string | null;
    canOpenOrder: boolean;
    dirtyByOrder: Record<string, Record<string, PurchaseOrderPartAction>>;
    daysByOrder: Record<string, Record<string, number>>;
    selectedByOrder: Record<string, Set<string>>;
    selectedTotal: number;
    onSetPartAction: (
        purchaseOrderId: string,
        partId: string,
        action: PurchaseOrderPartAction | undefined,
    ) => void;
    onSetDays: (purchaseOrderId: string, partId: string, days: number) => void;
    onTogglePartSelected: (purchaseOrderId: string, partId: string, checked: boolean) => void;
    onToggleSelectAllPending: (request: PurchaseOrderEntry, checked: boolean) => void;
    onClearSelection: () => void;
    onBulkAction: (action: PurchaseOrderPartAction) => void;
}

const EMPTY_SET: Set<string> = new Set();

export function PurchaseOrdersTable({
    requests,
    focusedPurchaseOrderId,
    canOpenOrder,
    dirtyByOrder,
    daysByOrder,
    selectedByOrder,
    selectedTotal,
    onSetPartAction,
    onSetDays,
    onTogglePartSelected,
    onToggleSelectAllPending,
    onClearSelection,
    onBulkAction,
}: PurchaseOrdersTableProps) {
    return (
        <Box position="relative" flex={1} minH={0} display="flex" flexDirection="column">
            <Box
                flex={1}
                minH={0}
                overflow="auto"
                sx={{
                    overscrollBehavior: 'contain',
                    scrollbarWidth: 'thin',
                }}
            >
                <Table variant="simple" size="sm" minW="100%">
                    <Thead position="sticky" top={0} zIndex={1}>
                        <Tr>
                            <Th {...HEADER_TH_PROPS} {...PO_CONTROL_CELL_PROPS} />
                            <Th {...HEADER_TH_PROPS}>RO</Th>
                            <Th {...HEADER_TH_PROPS}>Status</Th>
                            <Th {...HEADER_TH_PROPS}>Internal Fulfiller</Th>
                            <Th {...HEADER_TH_PROPS}>Vehicle</Th>
                            <Th {...HEADER_TH_PROPS}>Location</Th>
                            <Th {...HEADER_TH_PROPS}>Time Ordered</Th>
                            <Th {...HEADER_TH_PROPS}>Parts Ordered</Th>
                            <Th {...HEADER_TH_PROPS}>Order Value</Th>
                            <Th {...HEADER_TH_PROPS}>ETA</Th>
                            <Th {...HEADER_TH_PROPS} pr={6} />
                        </Tr>
                    </Thead>
                    <Tbody color="fg.default">
                        {requests.map((request) => {
                            const id = request.purchaseOrderId;
                            const isFocused = !!focusedPurchaseOrderId && id === focusedPurchaseOrderId;
                            return (
                                <PurchaseOrderRow
                                    key={id}
                                    request={request}
                                    initiallyExpanded={isFocused}
                                    highlight={isFocused}
                                    canOpenOrder={canOpenOrder}
                                    dirtyActions={dirtyByOrder[id] ?? {}}
                                    daysDrafts={daysByOrder[id] ?? {}}
                                    selectedPartIds={selectedByOrder[id] ?? EMPTY_SET}
                                    onSetPartAction={(partId, action) =>
                                        onSetPartAction(id, partId, action)
                                    }
                                    onSetDays={(partId, days) => onSetDays(id, partId, days)}
                                    onTogglePartSelected={(partId, checked) =>
                                        onTogglePartSelected(id, partId, checked)
                                    }
                                    onToggleSelectAllPending={(checked) =>
                                        onToggleSelectAllPending(request, checked)
                                    }
                                />
                            );
                        })}
                    </Tbody>
                </Table>
            </Box>
            <BulkActionBar
                selectedCount={selectedTotal}
                actions={BULK_ACTIONS}
                onClearSelection={onClearSelection}
                onAction={(actionId) => {
                    if (actionId === 'confirm' || actionId === 'reject') {
                        onBulkAction(actionId);
                    }
                }}
            />
        </Box>
    );
}
